import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X, Trophy, ArrowRight, ClipboardCheck, Check, AlertTriangle, Lock, Unlock, PenLine } from "lucide-react";
import { initials, dayLeaderboard } from "@/lib/scoring";
import { groupStatus } from "@/lib/verify";
import { CardDiscrepancies } from "@/screens/CardDiscrepancies";
import { GroupCardReview } from "@/screens/GroupCardReview";

const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };

/**
 * "Sign & submit card" sheet, opened manually from hole 18 / the round banner.
 * Shows missing holes, card disagreements, a hole-by-hole review, per-player signatures and the group standings.
 */
export default function RoundSummaryModal({ open, onClose, onGoLive, event, day, groupIdx: rawGroupIdx, scorerId, updateScores, canEdit, onJumpHole }) {
  const groupIdx = Math.max(0, Math.min(rawGroupIdx, (day?.groups?.length || 1) - 1));
  const status = open && day ? groupStatus(day, groupIdx) : null;
  const group = status?.group;
  const scorerInGroup = !!(group && scorerId && group.playerIds.includes(scorerId));
  const canAttest = !!status && status.complete && status.discrepancies.length === 0 && !status.submitted;

  if (!open || !day || !group) return null;
  const course = event.courses[day.courseKey];
  if (!course) return null;
  const nameOf = (pid) => event.players.find((p) => p.id === pid)?.name || "?";
  const groupRows = dayLeaderboard(day, event).filter((r) => group.playerIds.includes(r.player.id));
  const attestOp = (pid, value, onBehalf = false) => ({ dayId: day.id, groupIdx, holeIdx: 0, playerId: pid, attest: { value, by: scorerId, onBehalf } });
  const attest = (pid) => { updateScores([attestOp(pid, true, pid !== scorerId)]); toast.success(pid === scorerId ? "Card attested" : `Attested for ${nameOf(pid)}`); };
  const attestAll = () => { updateScores(status.pendingAttest.map((pid) => attestOp(pid, true, pid !== scorerId))); toast.success("Card submitted for the group"); };
  const withdraw = (pid) => updateScores([attestOp(pid, false)]);
  const reopen = () => { updateScores([{ dayId: day.id, groupIdx, holeIdx: 0, submit: { value: false } }]); toast.info("Group reopened for corrections"); };
  const letter = String.fromCharCode(65 + groupIdx);

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="round-summary-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[color:var(--bg-dark-green)] border border-[color:var(--border-brass)] rounded-t-2xl sm:rounded-2xl max-h-[92vh] flex flex-col tga-fade">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--border-brass-light)]">
          <div className="flex items-center gap-2">
            {status.submitted ? <Lock size={18} className="text-emerald-400" /> : status.complete ? <Trophy size={18} className="text-[color:var(--brass-bright)]" /> : <ClipboardCheck size={18} className="text-[color:var(--brass-bright)]" />}
            <div className="font-display text-xl text-[color:var(--brass-bright)]" data-testid="round-summary-title">
              {status.submitted ? "Card submitted" : status.complete ? "Round complete" : "Sign card"}
            </div>
          </div>
          <button data-testid="round-summary-close" onClick={onClose} className="ghost-btn rounded-lg p-2"><X size={16} /></button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          <div className="text-xs uppercase tracking-wide text-[color:var(--muted-green)]">{day.label} · Group {letter} · {course.name}</div>

          {status.submitted && (
            <div className="rounded-xl border border-emerald-500/40 bg-emerald-900/30 p-3 text-sm text-emerald-100 flex items-start gap-2" data-testid="round-summary-submitted">
              <Check size={16} className="mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="font-semibold">Group {letter}'s card is in · {timeOf(status.submitted.at)}</div>
                <div className="text-xs opacity-80">{status.submitted.byOrganizer ? "Closed by the organiser." : status.submitted.auto ? "All cards agreed — submitted automatically." : "Attested by every player."} Scores are locked.</div>
              </div>
              {canEdit && <button data-testid="round-summary-reopen" className="ghost-btn rounded-lg px-2.5 py-1.5 text-xs inline-flex items-center gap-1" onClick={reopen}><Unlock size={12} /> Reopen</button>}
            </div>
          )}

          {!status.submitted && status.missingHoles.length > 0 && (
            <div className="rounded-xl border border-amber-500/40 bg-amber-900/20 p-3" data-testid="round-summary-missing-holes">
              <div className="text-sm text-amber-100 flex items-center gap-2 mb-2"><AlertTriangle size={14} /> {status.missingHoles.length} hole{status.missingHoles.length === 1 ? "" : "s"} still need scores — tap to jump</div>
              <div className="flex flex-wrap gap-1.5">
                {status.missingHoles.map((h) => (
                  <button key={h} data-testid={`round-summary-jump-${h}`} className="brass-btn rounded-lg px-3 py-2 text-sm font-mono-metrics min-w-[48px]" onClick={() => { onJumpHole(h); onClose(); }}>{h + 1}</button>
                ))}
              </div>
            </div>
          )}

          {!status.submitted && status.discrepancies.length > 0 && (
            <div className="tga-card p-3" data-testid="round-summary-discrepancies">
              <div className="text-sm font-medium flex items-center gap-2 text-red-800 mb-1"><AlertTriangle size={14} /> Cards disagree on {status.discrepancies.length} hole{status.discrepancies.length === 1 ? "" : "s"} — tap the correct score</div>
              <CardDiscrepancies event={event} day={day} gi={groupIdx} discrepancies={status.discrepancies} updateScores={updateScores} testPrefix="round-summary" />
            </div>
          )}

          {!status.submitted && status.complete && status.missingCards.length > 0 && status.discrepancies.length === 0 && (
            <div className="text-xs text-[color:var(--muted-green)] flex flex-wrap gap-1.5 items-center" data-testid="round-summary-missing-cards">
              <span>Not keeping a full card:</span>
              {status.missingCards.map((m) => <span key={m.scorerId} className="rounded-full border border-[color:var(--border-brass-light)] px-2 py-0.5">{nameOf(m.scorerId)}</span>)}
            </div>
          )}

          <GroupCardReview event={event} day={day} group={group} scorerId={scorerId} locked={!!status.submitted} onJumpHole={(h) => { onJumpHole(h); onClose(); }} />

          {/* Attestations */}
          <div className="tga-card p-3" data-testid="round-summary-attest">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wide text-[color:var(--brass-dark)]">Signatures · {status.attested.length}/{group.playerIds.length}</div>
              {canAttest && scorerInGroup && status.pendingAttest.length > 1 && (
                <button data-testid="round-summary-attest-all" className="ghost-btn rounded-lg px-2.5 py-1.5 text-xs" onClick={attestAll}>Sign for everyone</button>
              )}
            </div>
            <div className="space-y-2">
              {group.playerIds.map((pid) => {
                const a = group.attestations?.[pid];
                const mine = pid === scorerId;
                return (
                  <div key={pid} className="flex items-center gap-2" data-testid={`round-summary-attest-row-${pid}`}>
                    <div className="avatar" style={{ width: 28, height: 28, fontSize: 12 }}>{initials(nameOf(pid))}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{nameOf(pid)}{mine && <span className="text-[10px] uppercase ml-1 text-[color:var(--brass-dark)]">you</span>}</div>
                      <div className="text-[11px] text-[color:var(--text-dark)]/60">
                        {a ? <span className="text-emerald-700 inline-flex items-center gap-1"><Check size={11} /> {a.auto ? "Auto" : a.onBehalf ? `Signed by ${nameOf(a.by)}` : "Signed"} · {timeOf(a.at)}</span> : "Awaiting signature"}
                      </div>
                    </div>
                    {!status.submitted && a && (mine || canEdit) && (
                      <button data-testid={`round-summary-withdraw-${pid}`} className="ghost-btn rounded-lg px-2 py-1.5 text-xs" onClick={() => withdraw(pid)}>Undo</button>
                    )}
                    {canAttest && !a && scorerInGroup && (
                      <button
                        data-testid={`round-summary-attest-${pid}`}
                        className={`${mine ? "brass-btn" : "ghost-btn"} rounded-lg px-3 py-2 text-xs inline-flex items-center gap-1 min-h-[40px]`}
                        onClick={() => attest(pid)}
                      ><PenLine size={12} /> {mine ? "Sign my card" : "Sign for them"}</button>
                    )}
                  </div>
                );
              })}
            </div>
            {!status.submitted && !scorerInGroup && <p className="helptext text-[color:var(--text-dark)]/60 mt-2">Pick your name under “Scoring as” to sign this card.</p>}
            {!status.submitted && status.complete && status.discrepancies.length > 0 && <p className="helptext text-red-800 mt-2">Resolve the disagreements above before signing.</p>}
            {!status.submitted && !status.complete && <p className="helptext text-[color:var(--text-dark)]/60 mt-2">All 18 holes must be scored before the card can be signed.</p>}
          </div>

          <div className="tga-card p-3">
            <table className="w-full text-sm font-mono-metrics">
              <thead>
                <tr className="text-left text-[10px] uppercase text-[color:var(--brass-dark)] border-b border-[rgba(27,51,34,0.15)]">
                  <th className="py-1">#</th>
                  <th className="py-1">Player</th>
                  <th className="py-1 text-right">Str</th>
                  <th className="py-1 text-right">FH</th>
                  <th className="py-1 text-right">GIR</th>
                  <th className="py-1 text-right">Pts</th>
                </tr>
              </thead>
              <tbody>
                {groupRows.map((r, i) => (
                  <tr key={r.player.id} className="tga-row border-b border-[rgba(27,51,34,0.08)]">
                    <td className="py-2">{i + 1}</td>
                    <td className="py-2 flex items-center gap-2">
                      <div className="avatar" style={{ width: 26, height: 26, fontSize: 12 }}>{initials(r.player.name)}</div>
                      <span className="font-sans">{r.player.name}</span>
                      {i === 0 && r.points > 0 && <Trophy size={12} className="text-[color:var(--brass)]" />}
                    </td>
                    <td className="py-2 text-right">{r.strokes}</td>
                    <td className="py-2 text-right">{r.fhAttempts ? `${r.fhHit}/${r.fhAttempts}` : "—"}</td>
                    <td className="py-2 text-right">{r.holesPlayed ? `${r.girHit}/${r.holesPlayed}` : "—"}</td>
                    <td className="py-2 text-right font-bold">{r.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="p-3 border-t border-[color:var(--border-brass-light)] flex gap-2">
          <button data-testid="round-summary-review" className="ghost-btn rounded-lg px-4 py-3 text-sm flex-1" onClick={onClose}>
            {status.submitted ? "Close" : "Back to scoring"}
          </button>
          <button data-testid="round-summary-end" className="brass-btn rounded-lg px-4 py-3 text-sm flex-1 inline-flex items-center justify-center gap-1" onClick={onGoLive}>
            Live leaderboard <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
